import React, { useEffect, useState } from "react";
import styles from "./newuserModal.module.css";
import { useNavigate } from "react-router-dom";

const TRANSITION_MS = 200;

const NewUserPopup = ({
  // mount control
  isOpen = false, // usually showGuideCard || showWelcomeCard
  // which cards to show
  showGuideCard = false, // “To get started”
  showWelcomeCard = false, // “Welcome to CUPI”
  // independent close handlers
  onCloseGuide = () => {}, // close only the guide card
  onCloseWelcome = () => {}, // close only the welcome card
  // overlay close (close both by default)
  onOverlayClose, // optional; if not provided, closes both
  onProceed = () => {},
  mascotSrc,
}) => {
  // keep mounted long enough to play close animation
  const [render, setRender] = useState(isOpen);
  const [success, setSuccess] = useState(false);
  const [phase, setPhase] = useState(isOpen ? "open" : "close"); // "open" | "close"
  const navigate = useNavigate();
  useEffect(() => {
    if (isOpen) {
      setRender(true);
      requestAnimationFrame(() => setPhase("open"));
    } else {
      setPhase("close");
      const t = setTimeout(() => setRender(false), TRANSITION_MS);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // lock scroll + ESC
  useEffect(() => {
    if (!render) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        // ESC closes the top-most first; tweak as needed
        if (showWelcomeCard) onCloseWelcome();
        else if (showGuideCard) onCloseGuide();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [render, showGuideCard, showWelcomeCard, onCloseGuide, onCloseWelcome]);

  if (!render) return null;

  const isOpening = phase === "open";
  const handleOverlay = () => {
    if (onOverlayClose) return onOverlayClose();
    // default: close both if open
    if (showWelcomeCard) onCloseWelcome();
    if (showGuideCard) onCloseGuide();
  };

  return (
    <>
      <div
        className={`${styles.overlay} ${
          isOpening ? styles.isOpen : styles.isClosing
        }`}
      />

      {showGuideCard && (
        <div
          className={styles.popupcontainer0}
          role="dialog"
          aria-modal="true"
          aria-labelledby="guideTitle"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`${styles.popupcontainer1} ${styles.guideCard} ${
              isOpening ? styles.cardOpen : styles.cardClose
            }`}
          >
            <div className={styles.popupcontainer1HeaderContainer0}>
              <div id="guideTitle" className={styles.popupcontainer1Header0}>
                To get started:
              </div>

              <button
                type="button"
                className={styles.closeBtn}
                onClick={onCloseGuide} // ← closes only this one
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className={styles.popupcontainerContent}>
              <ul className={styles.bullets}>
                <li>
                  Add the employee details and set transaction limits here.
                </li>
                <li>
                  Click <strong>Submit</strong> to send the KYC request to the
                  employee and once the KYC is complete,
                </li>
                <li>
                  Your employee can download our CUPI app and start spending.
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* WELCOME MODAL */}
      {showWelcomeCard && (
        <div
          className={styles.popupcontainer}
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcomeTitle"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`${styles.popupcontainer1} ${
              isOpening ? styles.cardOpen : styles.cardClose
            }`}
          >
            <div className={styles.popupcontainer1HeaderContainer}>
              <div id="welcomeTitle" className={styles.popupcontainer1Header}>
                Welcome to CUPI
              </div>

              <button
                type="button"
                className={styles.closeBtn}
                onClick={onCloseWelcome} // ← closes only this one
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <p className={styles.subhead}>
              We are thrilled to have you Onboard!
            </p>
            <div className={styles.popupcontainerContent}>
              <ul className={styles.bullets}>
                <li>
                  <strong>Let’s begin!</strong>
                </li>
                <li>
                  Add your first employee here. We’ll send them a KYC link, and
                  once it’s completed, they can download the CUPI app and start
                  spending.
                </li>
                <li>
                  To use the CUPI app, please complete your CUPI KYC. We’ve sent
                  the KYC link to your phone/email. Just click the link and
                  follow the steps.
                </li>
                <li>
                  As a welcome gift, we have loaded <strong>₹20</strong> into
                  your CUPI wallet to get you started.
                </li>
                <li>Happy Spending!</li>
              </ul>

              <p className={styles.subhead}>
                Complete your <strong>Advance KYC</strong> to unlock unlimited
                spending from your CUPI wallet
              </p>

              <div className={styles.ctaRow}>
                <button
                  type="button"
                  className={styles.cta}
                  onClick={() => navigate("/content/kycValidation?tab=1")}
                >
                  Proceed to Advance KYC
                </button>
              </div>

              <div className={styles.mascotWrap}>
                {mascotSrc ? (
                  <img
                    className={styles.mascot}
                    src="/cupiImg.svg"
                    alt="CUPI mascot"
                    draggable="false"
                  />
                ) : (
                  <div className={styles.mascotPlaceholder} aria-hidden />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewUserPopup;
